'use client';

/**
 * ResultsGrouped — owns the selection state across every group/case and
 * renders the sticky bottom CTA. Auto-ticked groups are selected by
 * default on mount; the user can toggle per group or per row.
 *
 * Emits a single `onClaim(keys)` when the user hits the CTA — the parent
 * decides whether to open the OTP modal or POST directly.
 */

import { useEffect, useMemo, useState } from 'react';
import GroupCard from './GroupCard';
import { caseKey } from './caseKey';
import type { ResultGroup } from '../types';

interface ResultsGroupedProps {
  groups: ResultGroup[];
  totalCases: number;
  activeCases: number;
  onClaim: (selectedCaseKeys: string[], selectedGroupKeys: string[]) => void;
  claiming?: boolean;
}

function initialSelection(groups: ResultGroup[]): Set<string> {
  const selected = new Set<string>();
  for (const g of groups) {
    if (!g.autoTicked) continue;
    for (const c of g.cases ?? []) selected.add(caseKey(c));
  }
  return selected;
}

export default function ResultsGrouped({
  groups,
  totalCases,
  activeCases,
  onClaim,
  claiming = false,
}: ResultsGroupedProps) {
  const [selected, setSelected] = useState<Set<string>>(() => initialSelection(groups));

  // If the groups prop swaps (e.g., OAuth hydration), reset to defaults.
  useEffect(() => {
    setSelected(initialSelection(groups));
  }, [groups]);

  const toggleCase = (key: string, next: boolean) => {
    setSelected(prev => {
      const s = new Set(prev);
      if (next) s.add(key);
      else s.delete(key);
      return s;
    });
  };

  const toggleGroup = (groupKey: string, next: boolean) => {
    const group = groups.find(g => g.groupingKey === groupKey);
    if (!group) return;
    setSelected(prev => {
      const s = new Set(prev);
      for (const c of group.cases ?? []) {
        const key = caseKey(c);
        if (next) s.add(key);
        else s.delete(key);
      }
      return s;
    });
  };

  // Group keys that have at least one case selected — what we persist as
  // `advocate_name_variants` on the profile after claim.
  const selectedGroupKeys = useMemo(() => {
    const out: string[] = [];
    for (const g of groups) {
      if ((g.cases ?? []).some(c => selected.has(caseKey(c)))) out.push(g.groupingKey);
    }
    return out;
  }, [groups, selected]);

  const selectedCount = selected.size;

  if (groups.length === 0) {
    return (
      <section className="rounded-xl border border-line bg-white p-8 text-center">
        <p className="font-serif text-xl text-ink">No cases found.</p>
        <p className="mt-2 text-sm text-ink-4">
          If you expected matches here, try a different spelling of your name,
          or a city where you have appeared.
        </p>
      </section>
    );
  }

  return (
    <section aria-label="Your cases">
      <header className="mb-4">
        <p className="eyebrow mb-2">Results</p>
        <h2 className="font-serif text-2xl text-ink sm:text-3xl">
          Found <span className="italic">{totalCases}</span> case
          {totalCases === 1 ? '' : 's'} across {groups.length} name variant
          {groups.length === 1 ? '' : 's'}.
        </h2>
        <p className="mt-2 text-sm text-ink-3">
          {activeCases} case{activeCases === 1 ? '' : 's'} with upcoming or recent hearings.
          Confirm which spellings are really you, then claim.
        </p>
      </header>

      <div className="space-y-4 pb-28">
        {groups.map(g => (
          <GroupCard
            key={g.groupingKey}
            group={g}
            selectedKeys={selected}
            onToggleCase={toggleCase}
            onToggleGroup={toggleGroup}
          />
        ))}
      </div>

      <StickyCTA
        selectedCount={selectedCount}
        claiming={claiming}
        onClaim={() => onClaim(Array.from(selected), selectedGroupKeys)}
      />
    </section>
  );
}

function StickyCTA({
  selectedCount,
  claiming,
  onClaim,
}: {
  selectedCount: number;
  claiming: boolean;
  onClaim: () => void;
}) {
  const disabled = selectedCount === 0 || claiming;
  return (
    <div
      className="fixed inset-x-0 bottom-0 z-20 border-t border-line bg-paper-elev/95 backdrop-blur supports-[backdrop-filter]:bg-paper-elev/80"
      role="region"
      aria-label="Claim bar"
    >
      <div className="mx-auto flex max-w-2xl flex-wrap items-center gap-4 px-4 py-4 sm:px-6">
        <div className="flex-1 min-w-0 text-sm text-ink-2">
          {selectedCount === 0
            ? 'Tick at least one group or case to continue.'
            : (
              <>
                <span className="tabular-nums font-medium">{selectedCount}</span> case
                {selectedCount === 1 ? '' : 's'} ready to import.
              </>
            )}
        </div>
        <button
          type="button"
          disabled={disabled}
          onClick={onClaim}
          className="rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm transition
                     hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-300
                     focus:ring-offset-2 disabled:bg-ink-5 disabled:cursor-not-allowed"
        >
          {claiming
            ? 'Claiming…'
            : selectedCount === 0
              ? 'Claim cases'
              : `Claim ${selectedCount} case${selectedCount === 1 ? '' : 's'} →`}
        </button>
      </div>
    </div>
  );
}
